import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import API from "../utils/api";
import { useAuth } from "../context/AuthContext";
import RevenueLineChart from "../components/admin/RevenueLineChart";
import {
  FiClock,
  FiAlertTriangle,
  FiStar,
  FiBriefcase,
  FiSlash,
} from "react-icons/fi";

/* ─── helpers ──────────────────────────────────────────────────────── */
const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtRs = (n) => `₹${fmt(n)}`;

const STATUS_COLORS = {
  draft: { bg: "#f59e0b20", text: "#f59e0b" },
  confirmed: { bg: "#3b82f620", text: "#3b82f6" },
  delivered: { bg: "#05966920", text: "#059669" },
  cancelled: { bg: "#ef444420", text: "#ef4444" },
};

/* ─── SectionCard ──────────────────────────────────────────────────── */
const SectionCard = ({ title, children, action, center }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-lg)",
      padding: 24,
      ...(center ? { display: "flex", flexDirection: "column" } : {}),
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 20,
      }}
    >
      <h3 style={{ fontSize: "0.95rem", fontWeight: 700, margin: 0 }}>
        {title}
      </h3>
      {action}
    </div>
    {center ? (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {children}
      </div>
    ) : (
      children
    )}
  </div>
);

/* ─── Revenue Line Chart lives in components/admin/RevenueLineChart.js ─── */

/* ─── Pipeline Donut ───────────────────────────────────────────────── */
const PipelineDonut = ({ pipeline, productionHealth }) => {
  const stages = [
    { key: "confirmed", label: "Confirmed", color: "#3b82f6", value: pipeline?.confirmed || 0 },
    { key: "delivered", label: "Delivered", color: "#059669", value: pipeline?.delivered || 0 },
    { key: "cancelled", label: "Cancelled", color: "#ef4444", value: pipeline?.cancelled || 0 },
    { key: "inProduction", label: "In Production", color: "#8b5cf6", value: productionHealth?.inProduction || 0 },
    { key: "ready", label: "Ready to Dispatch", color: "#06b6d4", value: productionHealth?.readyNotDispatched || 0 },
  ];
  const total = stages.reduce((sum, st) => sum + st.value, 0);

  const size = 210;
  const stroke = 40;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const cx = size / 2;
  const cy = size / 2;

  // Pre-compute each segment's start/length so labels can be placed on the arc
  let acc = 0;
  const segments = stages
    .filter((st) => st.value > 0)
    .map((st) => {
      const frac = total ? st.value / total : 0;
      const seg = { ...st, frac, start: acc };
      acc += frac;
      return seg;
    });
  const gap = segments.length > 1 ? 2 : 0; // thin white separators

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 24,
        flexWrap: "wrap",
        padding: "8px 0",
      }}
    >
      {/* Donut */}
      <div style={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <g transform={`rotate(-90 ${cx} ${cy})`}>
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="#f1f5f9"
              strokeWidth={stroke}
            />
            {segments.map((sg) => {
              const len = sg.frac * c;
              return (
                <circle
                  key={sg.key}
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill="none"
                  stroke={sg.color}
                  strokeWidth={stroke}
                  strokeDasharray={`${Math.max(len - gap, 0)} ${c - Math.max(len - gap, 0)}`}
                  strokeDashoffset={-sg.start * c}
                >
                  <title>{`${sg.label}: ${sg.value} (${Math.round(sg.frac * 100)}%)`}</title>
                </circle>
              );
            })}
          </g>
          {/* Percentage labels on the chart */}
          {segments.map((sg) => {
            const pct = Math.round(sg.frac * 100);
            if (sg.frac < 0.04) return null; // too thin to fit a label
            const ang = (sg.start + sg.frac / 2) * 2 * Math.PI - Math.PI / 2;
            return (
              <text
                key={`lbl-${sg.key}`}
                x={cx + r * Math.cos(ang)}
                y={cy + r * Math.sin(ang)}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#fff"
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  pointerEvents: "none",
                }}
              >
                {pct}%
              </text>
            );
          })}
        </svg>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              fontSize: "1.8rem",
              fontWeight: 800,
              color: "var(--text-primary, #111827)",
              fontFamily: "var(--font-display)",
              lineHeight: 1,
            }}
          >
            {total}
          </div>
          <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 4 }}>
            Total Orders
          </div>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 190 }}>
        {stages.map((st) => (
          <div
            key={st.key}
            style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem" }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: st.color,
                flexShrink: 0,
              }}
            />
            <span style={{ color: "var(--text-muted)", flex: 1 }}>{st.label}</span>
            <span
              style={{
                fontWeight: 700,
                color: st.color,
                fontFamily: "var(--font-display)",
                minWidth: 24,
                textAlign: "right",
              }}
            >
              {st.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Top Customers Table ──────────────────────────────────────────── */
const TopCustomers = ({ customers }) => {
  if (!customers || customers.length === 0)
    return (
      <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
        No order data yet.
      </p>
    );

  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.85rem",
        }}
      >
        <thead>
          <tr style={{ borderBottom: "2px solid var(--border)" }}>
            {["#", "Customer", "Orders", "Total Billed"].map((h) => (
              <th
                key={h}
                style={{
                  textAlign:
                    h === "Total Billed" || h === "Orders" ? "right" : "left",
                  padding: "6px 10px",
                  color: "var(--text-muted)",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {customers.map((c, i) => (
            <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
              <td
                style={{
                  padding: "10px",
                  color: "var(--text-muted)",
                  fontWeight: 700,
                }}
              >
                {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}`}
              </td>
              <td style={{ padding: "10px", fontWeight: 600 }}>
                {c.company || c.name || "—"}
              </td>
              <td style={{ padding: "10px", textAlign: "right" }}>
                {c.orderCount}
              </td>
              <td
                style={{
                  padding: "10px",
                  textAlign: "right",
                  fontWeight: 700,
                  color: "var(--primary)",
                }}
              >
                {fmtRs(c.totalBilled)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
═══════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════
   STAFF DASHBOARD — shown instead of full admin dashboard for staff
═══════════════════════════════════════════════════════════════════ */
const StaffDashboard = () => {
  const { user } = useAuth();
  // MySQL JSON columns can come back as strings — parse if needed
  const rawPages = user?.allowedPages || [];
  const allowedPages = typeof rawPages === 'string' ? (() => { try { return JSON.parse(rawPages); } catch { return []; } })() : rawPages;

  // Map page keys to readable labels and paths
  const PAGE_MAP = {
    'sales-orders':       { label: 'Sales Orders',       path: '/admin/sales-orders' },
    'order-detail':       { label: 'Order Detail',       path: '/admin/order-detail' },
    'production':         { label: 'Production',         path: '/admin/production' },
    'delivered':          { label: 'Delivered',          path: '/admin/delivered' },
    'payments':           { label: 'Receipts',           path: '/admin/payments' },
    'billing':            { label: 'Tax Invoice',        path: '/admin/billing' },
    'purchases':          { label: 'Purchases',          path: '/admin/purchases' },
    'expenses':           { label: 'Expenses',           path: '/admin/expenses' },
    'expense-categories': { label: 'Expense Categories', path: '/admin/expense-categories' },
    'users':              { label: 'Users',              path: '/admin/users' },
    'businesses':         { label: 'Businesses',         path: '/admin/businesses' },
    'contacts':           { label: 'Messages',           path: '/admin/contacts' },
    'vendors':            { label: 'Vendors',            path: '/admin/vendors' },
    'vendor-types':       { label: 'Vendor Types',       path: '/admin/vendor-types' },
    'ledger':             { label: 'Ledger',             path: '/admin/ledger' },
    'tds':                { label: 'TDS Register',       path: '/admin/tds' },
    'quotations':         { label: 'Quotations',         path: '/admin/quotations' },
    'products':           { label: 'Products',           path: '/admin/products' },
    'quote-requests':     { label: 'Quote Requests',     path: '/admin/quote-requests' },
    'public-services':    { label: 'Public Services',    path: '/admin/public-services' },
    'client-logos':       { label: 'Our Clients',        path: '/admin/client-logos' },
    'about-us':           { label: 'About Us',           path: '/admin/about-us' },
    'services':           { label: 'Services',           path: '/admin/services' },
  };

  // Parse allowed pages — strip :view/:edit suffix to get key
  const grantedPages = allowedPages
    .map(p => {
      const key = p.split(':')[0];
      const mode = p.split(':')[1] || 'edit';
      return { key, mode, ...(PAGE_MAP[key] || { label: key, path: '/admin' }) };
    })
    .filter(p => PAGE_MAP[p.key]);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 0 40px' }}>
      {/* Welcome */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)', margin: 0 }}>
          Welcome, {user?.name} 👋
        </h1>
        <div style={{ fontSize: '0.88rem', color: 'var(--text-muted)', marginTop: 4 }}>
          {user?.designation || 'Staff'} · {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
        </div>
      </div>

      {/* Granted pages grid */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
          Your accessible sections
        </div>
        {grantedPages.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', background: '#fff', border: '1px solid var(--border)', borderRadius: 12, color: 'var(--text-muted)' }}>
            No sections assigned yet. Contact your administrator.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {grantedPages.map(p => (
              <Link
                key={p.key}
                to={p.path}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px',
                  background: '#fff', border: '1px solid var(--border)', borderRadius: 12,
                  textDecoration: 'none', color: 'var(--text)',
                  transition: 'box-shadow 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{p.label}</span>
                <span style={{
                  fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20, fontWeight: 600,
                  background: p.mode === 'edit' ? '#ede9fe' : '#eff6ff',
                  color:      p.mode === 'edit' ? '#6d28d9'  : '#1d4ed8',
                }}>
                  {p.mode}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
═══════════════════════════════════════════════════════════════════ */
const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // All hooks must be declared before any early return (React rules of hooks)
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revenueRange, setRevenueRange] = useState("monthly");
  const [revenueData, setRevenueData] = useState({ current: [], previous: [], meta: {} });
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [todaysDeliveries, setTodaysDeliveries] = useState([]);
  const [pipelineRange, setPipelineRange] = useState("monthly");
  const [pipelineData, setPipelineData] = useState({
    draft: 0,
    confirmed: 0,
    delivered: 0,
    cancelled: 0,
  });
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [tdsSummary, setTdsSummary] = useState(null);   // TDS certificates still to collect

  useEffect(() => {
    if (user?.role === 'staff') return;
    API.get("/tds/summary").then((r) => setTdsSummary(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user?.role === 'staff') return; // skip admin-only API call
    API.get("/admin/stats")
      .then((r) => setStats(r.data))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (user?.role === 'staff') return;
    API.get("/admin/todays-deliveries").then((r) =>
      setTodaysDeliveries(r.data.orders || []),
    );
  }, [user]);

  useEffect(() => {
    if (user?.role === 'staff') return;
    setPipelineLoading(true);
    API.get(`/admin/pipeline?range=${pipelineRange}`)
      .then((r) => setPipelineData(r.data.pipeline || {}))
      .finally(() => setPipelineLoading(false));
  }, [pipelineRange, user]);

  useEffect(() => {
    if (user?.role === 'staff') return;
    setRevenueLoading(true);
    API.get(`/admin/revenue?range=${revenueRange}`)
      .then((r) => setRevenueData({
        current: r.data.current || [],
        previous: r.data.previous || [],
        meta: r.data.meta || {},
      }))
      .catch(() => setRevenueData({ current: [], previous: [], meta: {} }))
      .finally(() => setRevenueLoading(false));
  }, [revenueRange, user]);

  // Staff get their own simple dashboard — safe to return after all hooks
  if (user?.role === 'staff') return <StaffDashboard />;

  if (loading) return <div className="spinner" />;

  const { paymentHealth, productionHealth, topCustomers } = stats || {};
  const s = stats?.stats || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "1.35rem" }}>Dashboard</h2>
        <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
          {new Date().toLocaleDateString("en-IN", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </span>
      </div>

      {/* ── TDS certificates pending ── */}
      {tdsSummary?.totals?.pendingCount > 0 && (
        <div
          style={{
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            padding: "12px 16px", background: "#fffbeb", border: "1px solid #fde68a",
            borderRadius: 10, fontSize: "0.88rem", color: "#92400e",
          }}
        >
          <span>
            <strong>TDS to claim:</strong> {tdsSummary.totals.pendingCount} deduction
            {tdsSummary.totals.pendingCount > 1 ? "s" : ""} worth{" "}
            <strong>
              ₹{Number(tdsSummary.totals.pendingTds).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </strong>{" "}
            still waiting for the customer's TDS certificate.
          </span>
          <button
            onClick={() => navigate("/admin/tds")}
            style={{
              marginLeft: "auto", fontSize: "0.78rem", color: "#b45309", background: "none",
              border: "1px solid #f59e0b80", borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontWeight: 600,
            }}
          >
            Open TDS Register →
          </button>
        </div>
      )}

      {/* ── Alert banners ── */}
      {/* {(paymentHealth?.overdueCount > 0 ||
        productionHealth?.overdueDelivery > 0 ||
        s.pendingBusinessVerifications > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {paymentHealth?.overdueCount > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 16px",
                background: "#ef444410",
                border: "1px solid #ef444430",
                borderRadius: 10,
                fontSize: "0.85rem",
                color: "#ef4444",
                fontWeight: 600,
              }}
            >
              <FiAlertTriangle />
              {paymentHealth.overdueCount} credit order
              {paymentHealth.overdueCount > 1 ? "s are" : " is"} overdue —
              payment past due date.
              <button
                onClick={() => navigate("/admin/payments")}
                style={{
                  marginLeft: "auto",
                  fontSize: "0.78rem",
                  color: "#ef4444",
                  background: "none",
                  border: "1px solid #ef444450",
                  borderRadius: 6,
                  padding: "3px 10px",
                  cursor: "pointer",
                }}
              >
                View Payments →
              </button>
            </div>
          )}
          {productionHealth?.overdueDelivery > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 16px",
                background: "#f59e0b10",
                border: "1px solid #f59e0b30",
                borderRadius: 10,
                fontSize: "0.85rem",
                color: "#b45309",
                fontWeight: 600,
              }}
            >
              <FiClock />
              {productionHealth.overdueDelivery} order
              {productionHealth.overdueDelivery > 1 ? "s are" : " is"} past
              delivery date and not yet delivered.
              <button
                onClick={() => navigate("/admin/production")}
                style={{
                  marginLeft: "auto",
                  fontSize: "0.78rem",
                  color: "#b45309",
                  background: "none",
                  border: "1px solid #f59e0b50",
                  borderRadius: 6,
                  padding: "3px 10px",
                  cursor: "pointer",
                }}
              >
                View Production →
              </button>
            </div>
          )}
          {s.pendingBusinessVerifications > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 16px",
                background: "#3b82f610",
                border: "1px solid #3b82f630",
                borderRadius: 10,
                fontSize: "0.85rem",
                color: "#1d4ed8",
                fontWeight: 600,
              }}
            >
              <FiBriefcase size={14} />
              {s.pendingBusinessVerifications} business account
              {s.pendingBusinessVerifications > 1 ? "s are" : " is"} awaiting
              verification.
              <button
                onClick={() => navigate("/admin/businesses")}
                style={{
                  marginLeft: "auto",
                  fontSize: "0.78rem",
                  color: "#1d4ed8",
                  background: "none",
                  border: "1px solid #3b82f650",
                  borderRadius: 6,
                  padding: "3px 10px",
                  cursor: "pointer",
                }}
              >
                Verify Now →
              </button>
            </div>
          )}
        </div>
      )} */}

      {/* ── Revenue chart + Pipeline ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 20,
        }}
      >
        <SectionCard
          title="Revenue (Excluding GST)"
          action={
            <div style={{ display: "flex", gap: 4 }}>
              {["weekly", "monthly", "yearly"].map((r) => (
                <button
                  key={r}
                  onClick={() => setRevenueRange(r)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 20,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor:
                      revenueRange === r ? "var(--primary)" : "var(--border)",
                    background: revenueRange === r ? "var(--primary)" : "#fff",
                    color: revenueRange === r ? "#fff" : "var(--text-muted)",
                    transition: "all 0.15s",
                  }}
                >
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              ))}
            </div>
          }
        >
          {revenueLoading ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 0",
                color: "var(--text-muted)",
                fontSize: "0.85rem",
              }}
            >
              Loading...
            </div>
          ) : (
            <RevenueLineChart
              current={revenueData.current}
              previous={revenueData.previous}
              meta={revenueData.meta}
            />
          )}
        </SectionCard>

        <SectionCard
          title="Order Pipeline"
          center
          action={
            <div style={{ display: "flex", gap: 4 }}>
              {["weekly", "monthly", "yearly"].map((r) => (
                <button
                  key={r}
                  onClick={() => setPipelineRange(r)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 20,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor:
                      pipelineRange === r ? "var(--primary)" : "var(--border)",
                    background: pipelineRange === r ? "var(--primary)" : "#fff",
                    color: pipelineRange === r ? "#fff" : "var(--text-muted)",
                    transition: "all 0.15s",
                  }}
                >
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              ))}
            </div>
          }
        >
          {pipelineLoading ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 0",
                color: "var(--text-muted)",
                fontSize: "0.85rem",
              }}
            >
              Loading...
            </div>
          ) : (
            <PipelineDonut pipeline={pipelineData} productionHealth={productionHealth} />
          )}
        </SectionCard>
      </div>

      {/* ── Expenses This Month ── */}
      {/* {s.expensesThisMonth !== undefined && (
        <div
          onClick={() => navigate("/admin/expenses")}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 22px",
            background: "#fff",
            border: "1px solid var(--border)",
            borderRadius: 12,
            cursor: "pointer",
          }}
        >
          <div>
            <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Expenses This Month
            </div>
            <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "#dc2626", fontFamily: "var(--font-display)", marginTop: 4 }}>
              {fmtRs(s.expensesThisMonth)}
            </div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 2 }}>
              Confirmed expenses only
            </div>
          </div>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FiSlash size={20} color="#dc2626" />
          </div>
        </div>
      )} */}

      {/* ── Top Customers + Today's Deliveries ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 20,
        }}
      >
        <SectionCard
          title="Top 10 Customers"
          action={<FiStar size={14} color="#f59e0b" />}
        >
          <TopCustomers customers={topCustomers} />
        </SectionCard>

        <SectionCard
          title={`Today's Deliveries ${todaysDeliveries.length > 0 ? `(${todaysDeliveries.length})` : ""}`}
          action={
            <button
              onClick={() => navigate("/admin/sales-orders")}
              style={{
                fontSize: "0.78rem",
                color: "var(--primary)",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              View All →
            </button>
          }
        >
          {todaysDeliveries.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "24px 0",
                color: "var(--text-muted)",
                fontSize: "0.85rem",
              }}
            >
              No deliveries scheduled for today.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.85rem",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    {["Order #", "Customer", "Amount", "Type", "Status"].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: "left",
                            padding: "6px 12px",
                            color: "var(--text-muted)",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {todaysDeliveries.map((o) => {
                    const sc = STATUS_COLORS[o.status] || {
                      bg: "#99999920",
                      text: "#999",
                    };
                    return (
                      <tr
                        key={o.id}
                        style={{ borderBottom: "1px solid var(--border)" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f8f8f8")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <td
                          style={{
                            padding: "11px 12px",
                            fontWeight: 700,
                            color: "var(--primary)",
                          }}
                        >
                          {o.orderNumber}
                        </td>
                        <td style={{ padding: "11px 12px" }}>
                          <div style={{ fontWeight: 600 }}>
                            {o.companyName || o.customerName}
                          </div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--text-muted)",
                            }}
                          >
                            {o.companyName ? o.customerName : o.customerPhone}
                          </div>
                        </td>
                        <td style={{ padding: "11px 12px", fontWeight: 700 }}>
                          {fmtRs(o.grandTotal)}
                        </td>
                        <td style={{ padding: "11px 12px" }}>
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: 100,
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              background:
                                o.paymentType === "credit"
                                  ? "#3b82f615"
                                  : "#05966915",
                              color:
                                o.paymentType === "credit"
                                  ? "#3b82f6"
                                  : "#059669",
                            }}
                          >
                            {o.paymentType}
                          </span>
                        </td>
                        <td style={{ padding: "11px 12px" }}>
                          <span
                            style={{
                              padding: "2px 10px",
                              borderRadius: 100,
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              background: sc.bg,
                              color: sc.text,
                            }}
                          >
                            {o.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default AdminDashboard;